import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, V1 } from "@/lib/api";
import { Calendar, Clock, ArrowLeft, School as SchoolIcon } from "lucide-react";
import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";

function markdownToHtml(md) {
  if (!md) return "";
  // very lightweight md -> html; enough for our AI markdown
  let html = md
    .replace(/^# (.*)$/gm, "<h1>$1</h1>")
    .replace(/^## (.*)$/gm, "<h2>$1</h2>")
    .replace(/^### (.*)$/gm, "<h3>$1</h3>")
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/\*(.+?)\*/g, "<em>$1</em>")
    .replace(/\n\n+/g, "</p><p>")
    .replace(/\n- (.*?)(?=\n|$)/g, "<li>$1</li>");
  html = "<p>" + html + "</p>";
  html = html.replace(/(<li>[\s\S]+?<\/li>)/g, "<ul>$1</ul>");
  html = html.replace(/<p>(<h[1-3]>[\s\S]+?<\/h[1-3]>)<\/p>/g, "$1");
  html = html.replace(/<p>(<ul>[\s\S]+?<\/ul>)<\/p>/g, "$1");
  return html;
}

export default function SchoolArticle() {
  const { slug, articleSlug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`${V1}/schools/${slug}/articles/${articleSlug}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [slug, articleSlug]);

  if (loading) return <div className="p-16 text-center text-muted-foreground">Yükleniyor…</div>;
  if (!data) return <div className="p-16 text-center">Makale bulunamadı.</div>;

  const { school, article } = data;
  return (
    <div data-testid="school-article-page">
      <SEO
        title={article.seo_title || article.title}
        description={article.seo_description}
        canonical={`/okul/${school.slug}/makale/${article.slug}`}
        keywords={(article.keywords || []).join(", ")}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Article",
          headline: article.title,
          description: article.seo_description,
          datePublished: article.created_at,
          dateModified: article.updated_at,
          publisher: { "@type": "Organization", name: "SanoStudent", url: "https://bursluluksinavi.com" },
          about: {
            "@type": "EducationalOrganization",
            name: school.name,
            address: { "@type": "PostalAddress", addressLocality: school.district, addressRegion: school.province, addressCountry: "TR" },
          },
        }}
      />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Button asChild variant="ghost" size="sm" className="mb-6" data-testid="back-to-school">
          <Link to={`/okul/${school.slug}`}><ArrowLeft className="h-4 w-4" /> {school.name}</Link>
        </Button>
        <div className="overline text-accent flex items-center gap-2">
          <SchoolIcon className="h-3 w-3" /> {school.name} · {school.district}, {school.province}
        </div>
        <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-3">{article.title}</h1>
        <div className="mt-4 flex items-center gap-4 text-xs text-muted-foreground">
          <span className="flex items-center gap-1"><Calendar className="h-3.5 w-3.5" /> {new Date(article.created_at).toLocaleDateString("tr-TR")}</span>
          <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {article.read_minutes} dk okuma</span>
          <span>· Eğitim Öğretim {article.year}</span>
        </div>

        <article
          className="prose prose-slate max-w-none mt-8 prose-h1:font-display prose-h1:text-3xl prose-h2:font-display prose-h2:text-2xl prose-h2:mt-8 prose-h3:font-display prose-p:leading-relaxed"
          dangerouslySetInnerHTML={{ __html: markdownToHtml(article.content) }}
        />

        {(article.tags || []).length > 0 && (
          <div className="mt-10 pt-6 border-t border-border flex flex-wrap gap-2">
            {article.tags.map((t) => (
              <span key={t} className="px-2.5 py-1 rounded-full text-xs bg-muted">{t}</span>
            ))}
          </div>
        )}

        <div className="mt-12 p-6 rounded-xl border border-accent/40 bg-accent/5 text-center">
          <div className="overline text-accent mb-1">Okulla İletişim</div>
          <div className="font-display text-xl font-bold mb-3">{school.name}</div>
          <Button asChild data-testid="article-apply-cta">
            <Link to={`/okul/${school.slug}`}>Okul Sayfasına Dön</Link>
          </Button>
        </div>
      </div>
    </div>
  );
}
